import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { SearchService } from 'src/app/shared/services/metadata-api/search.service';
import { AuthService } from 'src/app/service/auth.service';
import { ConfigService } from 'src/app/service/config.service';
import { AiSearchService } from 'src/app/shared/services/ai-search/ai-search.service';
import { EntityRequestApiService } from 'src/app/shared/services/customer-api/entity-request-api.service';
import { QueryEditorComponent } from 'src/app/cockpit/main-news/news-query/query-editor/query-editor.component';
import {
  CommodityQueryRequest,
  EntityType,
  LexiconResponse,
  QueryFilter,
  TopicQueryRequest,
  YukkaQuery,
} from 'src/app/shared/models/ai-search.interface';
import { Entity } from 'src/app/shared/models/entity.interface';
import {
  CompanyFormComponent,
  CompanyFormData,
} from './company-form/company-form.component';

type DialogStep = 'select-type' | 'configure' | 'loading';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-ai-search-dialog',
  templateUrl: './ai-search-dialog.component.html',
  styleUrls: ['./ai-search-dialog.component.scss'],
})
export class AiSearchDialogComponent {
  step: DialogStep = 'select-type';
  errorMessage = '';

  entityType: EntityType = 'company';
  searchTerm = '';
  userEmail = '';
  project = '';

  companyFormValid = false;
  companyStep: 'search' | 'form' = 'search';
  companySearchEnabled = false;
  companyEnriching = false;
  @ViewChild(CompanyFormComponent) companyFormRef: CompanyFormComponent;
  private pendingCompanyData: CompanyFormData | null = null;

  // Topic fields
  topic = '';
  entityControl = new UntypedFormControl('');
  entityValue = '';
  entityResults$: Observable<Record<string, unknown>[]>;

  // Commodity fields
  commodity = '';

  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<AiSearchDialogComponent>,
    private router: Router,
    private searchService: SearchService,
    private auth: AuthService,
    private config: ConfigService,
    private aiSearchService: AiSearchService,
    private entityRequestApi: EntityRequestApiService,
  ) {
    this.searchTerm = this.aiSearchService.pendingLegalName ?? '';
    this.aiSearchService.pendingLegalName = null;
    this.userEmail = this.auth.authToken()?.email || '';
    this.project = this.config.appConfig.routing.project;
    this.setupEntitySearch();
  }

  private setupEntitySearch(): void {
    this.entityResults$ = this.entityControl.valueChanges.pipe(
      // Clear previous selection as soon as the user types again
      tap((value) => {
        if (typeof value === 'string') this.entityValue = '';
      }),
      debounceTime(500),
      switchMap((value) => {
        if (typeof value !== 'string' || value.length < 2) {
          return of([]);
        }
        return this.searchService.searchEntity(value).pipe(
          map((res) => res.hits),
          catchError(() => of([])),
        );
      }),
    );
  }

  displayEntity(option: unknown): string {
    if (!option || typeof option === 'string') return (option as string) || '';
    const entity = (option as Record<string, unknown>)['entity'] as Entity;
    return entity ? entity['name'] : '';
  }

  selectEntity(option: Record<string, unknown>): void {
    const entity = option['entity'] as Entity;
    this.entityValue = entity['compound_key'];
  }

  onEntityBlur(): void {
    if (typeof this.entityControl.value === 'string') {
      this.entityControl.setValue('');
      this.entityValue = '';
    }
  }

  selectType(type: EntityType): void {
    this.entityType = type;
  }

  goBack(): void {
    // Within company flow: go from form step back to search step
    if (this.entityType === 'company' && this.companyStep === 'form') {
      this.companyFormRef?.goBackToSearch();
      // goBackToSearch emits companyStepChange('search') which resizes the dialog
      return;
    }
    this.step = 'select-type';
    this.dialogRef.updateSize('');
  }

  continueToForm(): void {
    this.step = 'configure';
    if (this.entityType === 'company') {
      this.dialogRef.updateSize('500px');
    } else {
      this.dialogRef.updateSize('');
    }
    if (this.entityType === 'topic' && !this.topic && this.searchTerm) {
      this.topic = this.searchTerm;
    } else if (
      this.entityType === 'commodity' &&
      !this.commodity &&
      this.searchTerm
    ) {
      this.commodity = this.searchTerm;
    }
  }

  get isFormValid(): boolean {
    switch (this.entityType) {
      case 'company':
        return this.companyFormValid;
      case 'topic':
        return this.topic.trim().length > 0;
      case 'commodity':
        return this.commodity.trim().length > 0;
    }
  }

  onCompanyValidityChange(valid: boolean): void {
    this.companyFormValid = valid;
  }

  onNavigateToEntity(entity: { type: string; id: string }): void {
    this.dialog.closeAll();
    this.router.navigate(['/cockpit/news/market/chart'], {
      queryParams: {
        type: entity.type,
        id: entity.id,
        // Clear any leftover entity/event params from the previous route
        eventid: null,
        eventype: null,
        scoreevent: null,
        flagevent: null,
        tag: null,
        participant: null,
        newstype: null,
        focused_score: this.auth.scorelabSettings?.defaultScore || null,
      },
      queryParamsHandling: 'merge',
    });
  }

  onSearchStateChange(state: { enabled: boolean; enriching: boolean }): void {
    this.companySearchEnabled = state.enabled;
    this.companyEnriching = state.enriching;
  }

  onCompanyStepChange(step: 'search' | 'form'): void {
    this.companyStep = step;
    if (step === 'form') {
      this.dialogRef.updateSize('700px');
    } else {
      this.dialogRef.updateSize('500px');
    }
  }

  triggerCompanySearch(): void {
    this.companyFormRef?.onSearch();
  }

  triggerCompanySubmit(): void {
    this.companyFormRef?.onSubmit();
  }

  onCompanySubmit(data: CompanyFormData): void {
    this.pendingCompanyData = data;
    this.generateQuery();
  }

  generateQuery(): void {
    if (!this.isFormValid) {
      return;
    }

    this.step = 'loading';
    this.errorMessage = '';

    let request$: Observable<LexiconResponse>;

    switch (this.entityType) {
      case 'company': {
        if (!this.pendingCompanyData) return;
        request$ = this.aiSearchService.generateCompanyQuery(
          this.pendingCompanyData.request,
        );
        this.entityRequestApi
          .addRequestEntity(this.pendingCompanyData.entityRequestData)
          .subscribe({
            error: (err) => console.error('Entity request failed:', err),
          });
        this.pendingCompanyData = null;
        break;
      }
      case 'topic': {
        const request: TopicQueryRequest = {
          topic: this.topic.trim(),
          languages: this.aiSearchService.getLanguages(),
        };
        if (this.entityValue) request.entity = this.entityValue;

        request$ = this.aiSearchService.generateTopicQuery(request);
        break;
      }
      case 'commodity': {
        const request: CommodityQueryRequest = {
          commodity: this.commodity.trim(),
          languages: this.aiSearchService.getLanguages(),
        };
        request$ = this.aiSearchService.generateCommodityQuery(request);
        break;
      }
    }

    request$.subscribe({
      next: (response) => {
        this.dialog.closeAll();
        const query = this.setEntityFilter(response.search_query.query);
        const queryName = this.setQueryName(response, query);
        const dialogData = {
          name: queryName,
          description: this.truncateDescription(
            response.search_query.description,
          ),
          query,
          groups: [] as unknown[],
          _isAiGenerated: true as const,
        };
        this.aiSearchService.lastGeneratedQueryData = dialogData;
        this.dialog.open(QueryEditorComponent, {
          data: dialogData,
          exitAnimationDuration: 0,
        });
      },
      error: (err) => {
        this.step = 'configure';
        this.errorMessage =
          err?.error?.detail || 'Failed to generate query. Please try again.';
      },
    });
  }

  private setEntityFilter(query: YukkaQuery): YukkaQuery {
    const controlVal = this.entityControl.value as Record<string, unknown>;
    const entityName = controlVal?.['entity']
      ? (controlVal['entity'] as Entity)['name']
      : '';

    if (!entityName) return query;

    const patchFilter = (f: QueryFilter): QueryFilter =>
      f.field === 'entity' && !f.name
        ? { ...f, name: entityName, input: entityName }
        : f;

    return {
      ...query,
      filters: (query.filters || []).map(patchFilter),
    };
  }

  private setQueryName(
    searchQuery: LexiconResponse,
    query: YukkaQuery,
  ): string {
    const capitalize = (s: string): string =>
      s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    const topic = capitalize(searchQuery.lexicon.base_topic || '');
    const entityName =
      query.filters.find((f) => f.field === 'entity')?.name || '';

    switch (this.entityType) {
      case 'company':
        return searchQuery.search_query.name;
      case 'topic':
        return entityName ? `${topic} - ${entityName}` : topic;
      case 'commodity':
        return searchQuery.lexicon.name;
    }
  }

  private truncateDescription(description: string): string {
    if (!description || description.length <= 200) {
      return description;
    }
    const lastDot = description.lastIndexOf('.', 200);
    if (lastDot !== -1) {
      return description.slice(0, lastDot + 1);
    }
    return description.slice(0, 200);
  }
}
